import request from '@/utils/request';

/**
 * 用户管理相关 API
 * 与后端接口文档保持一致
 */

/**
 * 获取个人信息详情
 * @returns {Promise<Object>} 个人信息详情
 */
export function getProfileDetail() {
  return request({
    url: '/user/profile/detail',
    method: 'post'
  });
}

/**
 * 上传用户头像
 * @param {File} file - 头像文件
 * @returns {Promise<Object>} 上传结果，返回更新后的个人信息
 */
export function uploadAvatar(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request({
    url: '/user/profile/uploadAvatar',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

/**
 * 修改密码（用户侧）
 * @param {Object} data - 密码参数
 * @param {string} data.oldPassword - 旧密码
 * @param {string} data.newPassword - 新密码（8-20位，须含大写、小写字母和数字，特殊字符可选）
 * @returns {Promise<Object>} 修改结果
 */
export function updatePassword(data) {
  return request({
    url: '/user/profile/resetPassword',
    method: 'post',
    data
  });
}

/**
 * 分页查询用户列表
 * @param {Object} params - 查询参数
 * @param {number} params.pageNum - 页码（默认1）
 * @param {number} params.pageSize - 每页条数（默认10）
 * @param {string} params.keyword - 登录名、昵称、手机号（模糊查询）
 * @param {number} params.status - 用户状态（0-正常/启用，1-封禁/停用）
 * @param {Array}  params.orderByList - 排序参数
 * @returns {Promise<Object>} 用户列表数据
 */
export function getUserList(params) {
  return request({
    url: '/manager/user/page',
    method: 'post',
    data: {
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 10,
      keyword: params.keyword || undefined,
      status: params.status !== undefined && params.status !== null ? params.status : undefined,
      orderByList: params.orderByList || undefined
    }
  });
}

/**
 * 查询用户详情
 * @param {number} id - 用户ID
 * @returns {Promise<Object>} 用户详情
 */
export function getUserDetail(id) {
  return request({
    url: '/manager/user/detail',
    method: 'post',
    data: { id }
  });
}

/**
 * 创建用户
 * @param {Object} data - 用户数据
 * @param {string} data.email - 邮箱
 * @param {string} data.loginName - 登录名
 * @param {string} data.mobile - 手机号
 * @param {string} data.password - 密码
 * @param {string} data.username - 昵称
 * @returns {Promise<Object>} 创建结果
 */
export function createUser(data) {
  return request({
    url: '/manager/user/create',
    method: 'post',
    data: {
      email: data.email,
      loginName: data.loginName,
      mobile: data.mobile,
      password: data.password,
      username: data.username,
      userType: data.userType
    }
  });
}

/**
 * 更新用户
 * @param {number} id - 用户ID
 * @param {Object} data - 用户数据
 * @param {string} data.email - 邮箱（必填）
 * @param {string} data.mobile - 手机号码，格式 ^1[3-9]\d{9}$（必填）
 * @param {string} data.username - 昵称（必填）
 * @returns {Promise<Object>} 更新结果
 */
export function updateUser(id, data) {
  return request({
    url: '/manager/user/update',
    method: 'post',
    data: {
      id: id,
      email: data.email,
      mobile: data.mobile,
      username: data.username,
      userType: data.userType
    }
  });
}

/**
 * 删除用户
 * @param {number} id - 用户ID
 * @returns {Promise<Object>}
 */
export function deleteUser(id) {
  return request({
    url: '/manager/user/delete',
    method: 'post',
    data: { id }
  });
}

/**
 * 重置用户密码
 * @param {number} userId - 用户ID
 * @param {Object} data - 密码数据
 * @param {string} data.password - 新密码
 * @returns {Promise<Object>}
 */
export function resetPassword(userId, data) {
  return request({
    url: '/manager/user/resetPassword',
    method: 'post',
    data: {
      userId: userId,
      newPassword: data.password || data.newPassword
    }
  });
}

/**
 * 锁定用户
 * @param {number} id - 用户ID
 * @returns {Promise<Object>}
 */
export function lockUser(id) {
  return request({
    url: '/manager/user/lock',
    method: 'post',
    data: { id }
  });
}

/**
 * 解锁用户
 * @param {number} id - 用户ID
 * @returns {Promise<Object>}
 */
export function unlockUser(id) {
  return request({
    url: '/manager/user/unlock',
    method: 'post',
    data: { id }
  });
}

/**
 * 强制踢出用户
 * @param {number} id - 用户ID
 * @returns {Promise<Object>}
 */
export function kickoutUser(id) {
  return request({
    url: '/manager/user/kickOut',
    method: 'post',
    data: { id }
  });
}

/**
 * 获取用户充值记录列表
 * @param {Object} params - 查询参数
 * @param {number} params.userId - 用户 ID
 * @param {string} [params.keyword] - 搜索关键词（备注信息）
 * @param {number} [params.status] - 状态：0-失败，1-成功
 * @param {string} [params.startDate] - 开始日期
 * @param {string} [params.endDate] - 结束日期
 * @returns {Promise<Object>} 充值记录列表
 */
export function getUserRechargeList(params) {
  const data = {
    userId: typeof params === 'object' ? params.userId : params,
    keyword: typeof params === 'object' ? params.keyword : undefined,
    status: typeof params === 'object' ? params.status : undefined,
    startDate: typeof params === 'object' ? params.startDate : undefined,
    endDate: typeof params === 'object' ? params.endDate : undefined
  };
  return request({
    url: '/manager/user/recharge/list',
    method: 'post',
    data
  }).catch(() => {
    // 接口不存在时返回模拟数据
    // eslint-disable-next-line no-console
    console.warn('充值记录接口未实现，使用模拟数据');
    return Promise.resolve({
      code: 0,
      message: 'success',
      data: generateMockRechargeData(data.userId)
    });
  });
}

/**
 * 生成模拟充值数据
 */
function generateMockRechargeData(userId) {
  const methods = ['微信支付', '支付宝', '银行卡', '信用卡'];
  const remarks = ['月度会员充值', '季度会员充值', '年度会员充值', '积分充值', '活动赠送'];
  const list = [];
  for (let i = 0; i < 8; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i * 5);
    list.push({
      id: i + 1,
      userId: userId,
      amount: (Math.random() * 200 + 10).toFixed(2),
      points: Math.floor(Math.random() * 1000 + 100),
      method: methods[Math.floor(Math.random() * methods.length)],
      status: Math.random() > 0.2 ? 1 : 0,
      createTime: date.toISOString().split('T')[0],
      remark: remarks[Math.floor(Math.random() * remarks.length)]
    });
  }
  return list;
}

/**
 * 获取用户算力使用记录列表
 * @param {Object} params - 查询参数
 * @param {number} params.userId - 用户 ID
 * @param {string} [params.keyword] - 搜索关键词（模型名称）
 * @param {string} [params.taskType] - 任务类型
 * @param {number} [params.status] - 状态：0-待处理，1-成功，2-进行中，3-失败
 * @param {string} [params.startDate] - 开始日期
 * @param {string} [params.endDate] - 结束日期
 * @returns {Promise<Object>} 算力使用记录列表
 */
export function getUserComputeUsageList(params) {
  const data = {
    userId: typeof params === 'object' ? params.userId : params,
    keyword: typeof params === 'object' ? params.keyword : undefined,
    taskType: typeof params === 'object' ? params.taskType : undefined,
    status: typeof params === 'object' ? params.status : undefined,
    startDate: typeof params === 'object' ? params.startDate : undefined,
    endDate: typeof params === 'object' ? params.endDate : undefined
  };
  return request({
    url: '/manager/user/compute/usage/list',
    method: 'post',
    data
  });
}

/**
 * 充值积分 - 后端接口暂不可用
 * @param {number} id - 用户ID
 * @param {Object} data - 充值数据
 * @returns {Promise<Object>}
 */
export function rechargePoints(id, data) {
  // eslint-disable-next-line no-console
  console.warn('充值积分接口在后端接口文档中不存在，需要后端实现');
  return Promise.resolve({
    code: 0,
    data: null,
    message: '充值积分功能需要后端提供接口'
  });
}

/**
 * 设置用户可用模型 - 后端接口暂不可用
 * @param {number} id - 用户ID
 * @param {Object} data - 模型数据
 * @returns {Promise<Object>}
 */
export function setUserModels(id, data) {
  // eslint-disable-next-line no-console
  console.warn('设置可用模型接口在后端接口文档中不存在，需要后端实现');
  return Promise.resolve({
    code: 0,
    data: null,
    message: '设置可用模型功能需要后端提供接口'
  });
}

/**
 * 切换用户锁定状态（兼容旧接口）
 * @param {number} id - 用户ID
 * @param {Object} data - 状态数据
 * @param {number} data.status - 目标状态：1-启用，0-停用
 * @returns {Promise<Object>}
 */
export function toggleUserLock(id, data) {
  // 后端 status: 0-停用，1-启用
  // 如果目标是启用(1)，则调用解锁；如果目标是停用(0)，则调用锁定
  const targetStatus = data.status;
  if (targetStatus === 1 || targetStatus === 'active') {
    return unlockUser(id);
  }
  return lockUser(id);
}

/**
 * 券码充值
 * @param {Object} data - 充值参数
 * @param {string} data.voucherCode - 券码
 * @param {string} data.voucherPwd - 密码
 * @returns {Promise<Object>} 充值结果,返回更新后的个人信息
 */
export function rechargeByVoucher(data) {
  return request({
    url: '/user/profile/recharge',
    method: 'post',
    data: {
      voucherCode: data.voucherCode,
      voucherPwd: data.voucherPwd
    }
  });
}

/**
 * 常用语管理相关 API
 */

/**
 * 新增常用语
 * @param {Object} data - 常用语参数
 * @param {string} data.title - 标题（必填，0-128字符）
 * @param {string} data.content - 常用语内容（必填，0-512字符）
 * @param {number} [data.sortOrder] - 排序值（越小越靠前，不填则自动追加到末尾）
 * @returns {Promise<Object>} 新增结果
 */
export function createCommonPhrase(data) {
  return request({
    url: '/user/common-phrase/create',
    method: 'post',
    data: {
      title: data.title,
      content: data.content,
      sortOrder: data.sortOrder
    }
  });
}

/**
 * 删除常用语
 * @param {Object} data - 常用语参数
 * @param {number} data.id - 常用语ID（必填）
 * @returns {Promise<Object>} 删除结果
 */
export function deleteCommonPhrase(data) {
  return request({
    url: '/user/common-phrase/delete',
    method: 'post',
    data: {
      id: data.id
    }
  });
}

/**
 * 查询所有常用语列表
 * @returns {Promise<Object>} 常用语列表
 */
export function listAllCommonPhrases() {
  return request({
    url: '/user/common-phrase/listAll',
    method: 'post'
  });
}

/**
 * 分页查询常用语列表
 * @param {Object} params - 查询参数
 * @param {number} [params.pageNum] - 页码（默认1）
 * @param {number} [params.pageSize] - 每页条数（默认10）
 * @param {string} [params.keyword] - 关键词（模糊搜索标题或内容）
 * @param {Array} [params.orderByList] - 排序参数
 * @returns {Promise<Object>} 分页数据
 */
export function pageCommonPhrases(params) {
  return request({
    url: '/user/common-phrase/page',
    method: 'post',
    data: {
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 10,
      keyword: params.keyword || undefined,
      orderByList: params.orderByList || undefined
    }
  });
}

/**
 * 调整常用语顺序
 * @param {Object} data - 排序参数
 * @param {Array<number>} data.ids - 排序列表（按新顺序传入ID数组）
 * @returns {Promise<Object>} 调整结果
 */
export function sortCommonPhrases(data) {
  return request({
    url: '/user/common-phrase/sort',
    method: 'post',
    data: {
      ids: data.ids
    }
  });
}

/**
 * 编辑常用语
 * @param {Object} data - 常用语参数
 * @param {number} data.id - 常用语ID（必填）
 * @param {string} [data.title] - 标题（0-128字符）
 * @param {string} [data.content] - 常用语内容（0-512字符）
 * @returns {Promise<Object>} 更新结果
 */
export function updateCommonPhrase(data) {
  return request({
    url: '/user/common-phrase/update',
    method: 'post',
    data: {
      id: data.id,
      title: data.title,
      content: data.content
    }
  });
}

/**
 * 获取充值记录（简易分页）
 * @param {Object} params - 查询参数
 * @param {number} [params.pageNum=1] - 页码
 * @param {number} [params.pageSize=10] - 每页条数
 * @returns {Promise<Object>} 充值记录列表
 */
export function getRechargeSimplePage(data) {
  return request({
    url: '/user/profile/rechargeSimplePage',
    method: 'post',
    data: {
      pageNum: data.pageNum || 1,
      pageSize: data.pageSize || 10
    }
  });
}
